import { useEffect, useRef, useState } from "react";
import { audienceSentence, AudienceSchema, type Audience } from "../../../shared/goals/audience";
import { metaSettings } from "../../../shared/goals/meta";
import { PlacementsSchema, spotsText } from "../../../shared/goals/placements";
import { Link } from "react-router";
import { GoalConfigSchema, type GoalConfig } from "../../../shared/goals/config";
import { mergePatch } from "../../../shared/goals/patch";
import { Icon } from "../../components/ui/Icon";
import { Orb } from "../../components/ui/Orb";
import type { Need, ReadyLite } from "../../lib/ccApi";
import { initials } from "../goal/text";
import { changes } from "./editDiff";
import { needText, parseTime, usd } from "./text";
import { useCan, useMayDecide } from "../../context/AuthContext";

/** How long ago something was asked, as the prototype writes it ("2 hours ago"). */
export function ago(t: string, now = Date.now()): string {
  const m = Math.max(0, Math.round((now - parseTime(t).getTime()) / 60000));
  if (m < 2) return "Just now";
  if (m < 60) return `${m} minutes ago`;
  const h = Math.round(m / 60);
  if (h < 24) return h === 1 ? "1 hour ago" : `${h} hours ago`;
  const d = Math.round(h / 24);
  return d === 1 ? "Yesterday" : `${d} days ago`;
}

const str = (v: unknown) => (typeof v === "string" ? v : "");

/** An audience in the sentence the Audience step shows. */
const words = (a: Audience) => audienceSentence(a).map((x) => x.text).join("");

/** A need's change as before and after, when it changes the goal's config. */
export function needDiff(n: Need, config: GoalConfig | null) {
  if (n.kind === "budget.set")
    return [
      { kind: "rm" as const, text: `Daily budget: ${usd(Number(n.payload.fromCents) || 0)}` },
      { kind: "add" as const, text: `Daily budget: ${usd(Number(n.payload.toCents) || 0)}` },
    ];
  if (n.kind === "audience.update" && config) {
    const was = metaSettings(config.channels.meta?.settings).audience;
    const next = AudienceSchema.safeParse(n.payload.audience);
    return [...(was ? [{ kind: "rm" as const, text: words(was) }] : []), ...(next.success ? [{ kind: "add" as const, text: words(next.data) }] : [])];
  }
  if (n.kind === "placements.set" && config) {
    const next = PlacementsSchema.nullable().safeParse(n.payload.spots);
    return [
      { kind: "rm" as const, text: `Where the ads show: ${spotsText(metaSettings(config.channels.meta?.settings).placements)}` },
      ...(next.success ? [{ kind: "add" as const, text: `Where the ads show: ${spotsText(next.data)}` }] : []),
    ];
  }
  if (n.kind !== "goal.change" || !config) return [];
  const next = GoalConfigSchema.safeParse(mergePatch(config, n.payload.patch));
  return next.success ? changes(config, next.data).flatMap((c) => c.lines) : [];
}

/** The body of a need, by its kind: the ad, the email as it will go, the draft reply, the change. */
function NeedBody({ n, config, ready }: { n: Need; config: GoalConfig | null; ready: ReadyLite | null }) {
  const p = n.payload;
  if (n.kind === "ad.add" || n.kind === "ad.text") {
    // Why: the ad's words live in Get ready; the need names the ad.
    const w = ready?.ads.find((a) => a.id === p.adId)?.words ?? {};
    const primary = n.kind === "ad.text" ? str(p.primary) : str(w.primary);
    const headline = n.kind === "ad.text" ? str(p.headline) : str(w.headline);
    const name = n.context?.ad?.name ?? str(w.name);
    return (
      <div className="nd-t">
        {name && <b>{name}</b>}
        {str(w.says) && <p>{str(w.says).split("\n")[0]}</p>}
        {(primary || headline) && (
          <div className="rows rd-kv">
            {primary && (
              <div className="kv">
                <span>Text</span>
                <span>{primary}</span>
              </div>
            )}
            {headline && (
              <div className="kv">
                <span>Headline</span>
                <span>{headline}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
  if (n.kind === "email.step") {
    const step = Number(p.step) || 1;
    const mail = ready?.emails.find((e) => e.step === step);
    const list = n.context?.people ?? [];
    const people = list.length || (Array.isArray(p.people) ? p.people.length : 0);
    return (
      <>
        <div className="to">
          {list.length > 0 && (
            <div className="stack">
              {list.slice(0, 2).map((x) => (
                <i className="cc-face sm" key={x.id}>
                  {initials(x.name)}
                </i>
              ))}
              {list.length > 2 && <span>+{list.length - 2}</span>}
            </div>
          )}
          <span>
            {[str(p.market), `${people} ${people === 1 ? "person" : "people"}`, `email ${step}`].filter(Boolean).join(" · ")}
          </span>
        </div>
        {mail && (
          <div className="mail">
            <div className="m-lbl">{list[0] ? `Preview for ${list[0].name}` : "Preview"}</div>
            <div className="m-subj">{mail.subject}</div>
            {mail.body
              .split("\n")
              .filter((l) => l.trim() && l.trim() !== "{first line}")
              .map((l, i) => (
                <p key={i}>
                  {l.split(/(\{first name\})/).map((x, j) =>
                    x === "{first name}" ? (
                      <span className="tok" key={j}>
                        {list[0] ? list[0].name.split(" ")[0] : "first name"}
                      </span>
                    ) : (
                      x
                    ),
                  )}
                </p>
              ))}
          </div>
        )}
      </>
    );
  }
  if (n.kind === "email.reply")
    return (
      <div className="thread">
        {n.context?.inbound?.body && (
          <div className="bub them">
            <i className="cc-face sm">{initials(n.context.person?.name ?? "?")}</i>
            <div>
              <div className="bn">
                {n.context.person?.name ?? "They wrote"}
                {n.context.person?.org && <span> · {n.context.person.org}</span>}
              </div>
              <div className="bt">{n.context.inbound.body}</div>
            </div>
          </div>
        )}
        <div className="bub us">
          <div>
            <div className="bn">Draft from the agent</div>
            <div className="bt">
              {str(p.body)
                .split(/\n/)
                .map((l, i) => (
                  <span key={i}>
                    {i > 0 && <br />}
                    {l}
                  </span>
                ))}
            </div>
          </div>
        </div>
      </div>
    );
  const lines = needDiff(n, config);
  return lines.length ? (
    <div className="diff">
      {lines.map((l, i) => (
        <div className={`dl ${l.kind}`} key={i}>
          {l.text}
        </div>
      ))}
    </div>
  ) : null;
}

export type Decide = (n: Need, decision: "approve" | "skip") => Promise<boolean>;

function NeedCard({ n, config, ready, flash, onDecide, goalId }: { n: Need; config: GoalConfig | null; ready: ReadyLite | null; flash: boolean; onDecide: Decide; goalId: string }) {
  const ref = useRef<HTMLElement>(null);
  const [st, setSt] = useState<"" | "ok" | "gone">("");
  const [label, setLabel] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const can = useCan();
  const mayDecide = useMayDecide()(n);
  const t = needText(n);
  const why = str(n.payload.reason);
  const soft = (n.check?.checks ?? []).filter((c) => !c.ok);

  useEffect(() => {
    if (!flash || !ref.current) return;
    const el = ref.current;
    el.classList.remove("cc-flash");
    void el.offsetWidth;
    el.classList.add("cc-flash");
    el.scrollIntoView({ block: "center" });
  }, [flash]);

  const decide = async (d: "approve" | "skip") => {
    setBusy(true);
    const ok = await onDecide(n, d);
    setBusy(false);
    if (!ok) return;
    setLabel(d === "skip" ? "Skipped" : n.kind === "email.reply" ? "Sent" : "Approved");
    setSt("ok");
    const el = ref.current;
    const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
    setTimeout(
      () => {
        if (el) el.style.height = `${el.offsetHeight}px`;
        setSt("gone");
      },
      reduce ? 0 : 650,
    );
  };

  return (
    <article className={`need${st === "ok" ? " ok" : st === "gone" ? " ok gone" : ""}`} id={`n-${n.id}`} ref={ref}>
      <div className="need-top">
        <span className="kind">
          <Icon name={t.icon} />
          {t.kind}
        </span>
        <span className="need-cost mono">{ago(n.createdAt)}</span>
      </div>
      <h4>{t.title}</h4>
      <NeedBody n={n} config={config} ready={ready} />
      {why && (
        <p className="why">
          <b>Why:</b> {why}
        </p>
      )}
      {soft.length > 0 && (
        <div className="st-meta">
          <span className="chip rl">Checked against your rules</span>
          {soft.map((c) => (
            <span className="chip" key={c.id}>
              {c.text}
            </span>
          ))}
        </div>
      )}
      <div className="need-f">
        {mayDecide && (
          <button type="button" className="btn sm" disabled={busy || !!label} onClick={() => void decide("approve")}>
            <Icon name={label ? "check" : n.kind === "email.reply" ? "send" : "check"} />
            {label ?? (n.kind === "email.reply" ? "Send" : "Approve")}
          </button>
        )}
        {(n.kind === "ad.add" || n.kind === "email.step" || n.kind.startsWith("campaign.")) && !label && can("editor") && (
          <Link className="btn sm ghost" to={`/goals/${encodeURIComponent(goalId)}/ready`}>
            Open Get ready
          </Link>
        )}
        {!label && mayDecide && (
          <button type="button" className="skip" disabled={busy} onClick={() => void decide("skip")}>
            Skip
          </button>
        )}
      </div>
    </article>
  );
}

/** The Needs you panel: every decision waiting on this goal, one card each (the prototype's `renderNeeds`). */
export function NeedsList({ goalId, needs, config, ready, focus, review, onDecide }: { goalId: string; needs: Need[]; config: GoalConfig | null; ready: ReadyLite | null; focus: string | null; review: string; onDecide: Decide }) {
  return (
    <section className="a-needs" id="needs">
      <div className="needs-h">
        <h3>Needs you</h3>
        <span className={`ncount${needs.length ? "" : " clear"}`}>{needs.length ? `${needs.length} waiting` : "All clear"}</span>
      </div>
      <div className="needlist">
        {needs.map((n) => (
          <NeedCard key={n.id} n={n} config={config} ready={ready} flash={focus === n.id} onDecide={onDecide} goalId={goalId} />
        ))}
        {!needs.length && (
          <div className="card clear">
            <Orb live={false} />
            <span>
              <b>All clear.</b> {review}
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

/** Compact rows in the chat that always show the current state of what waits (the prototype's `.cc-nrows`). */
export function NeedRows({ rows, done, onOpen }: { rows: Need[]; done: { need: Need; label: string }[]; onOpen: (id: string) => void }) {
  if (!rows.length && !done.length)
    return (
      <p className="cc-fine" style={{ margin: 0 }}>
        Nothing is waiting for you.
      </p>
    );
  return (
    <div className="cc-nrows">
      {rows.map((n) => {
        const t = needText(n);
        return (
          <div className="cc-nr" key={n.id}>
            <span className="cc-nri">
              <Icon name={t.icon} />
            </span>
            <span className="cc-nrt">
              <b>{t.title}</b>
              <span>{str(n.payload.reason) || t.line}</span>
            </span>
            <button type="button" className="btn sm" onClick={() => onOpen(n.id)}>
              Review
            </button>
          </div>
        );
      })}
      {done.map(({ need, label }) => {
        const t = needText(need);
        return (
          <div className="cc-nr done" key={need.id}>
            <span className="cc-nri">
              <Icon name={t.icon} />
            </span>
            <span className="cc-nrt">
              <b>{t.title}</b>
              <span>{str(need.payload.reason) || t.line}</span>
            </span>
            <span className={`chip${label === "Skipped" ? "" : " ok"}`}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}
